import React, { useState, useRef } from 'react'
import styled from 'styled-components'

import Tabs from 'components/Tabs'
import Search from 'components/Search'
import ResourceTable from 'pages/Resources/ResourceTable'

import useAutoFocus from 'hooks/useAutoFocus'
import useResources from 'hooks/useResources'

import Resource from 'types/Resource'

import { color } from 'styles/colors'
import { PageHeader, PageTitle, screenSize } from 'styles/shared'

const Resources = () => {
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const { articles, faqs, videos, thermostatReleases, areResourcesLoaded } = useResources({ publishedOnly: true })

  const [displayFiltered, setDisplayFiltered] = useState(false)
  const [filteredVideos, setFilteredVideos] = useState<Array<Resource>>([])
  const [filteredArticles, setFilteredArticles] = useState<Array<Resource>>([])
  const [filteredFaqs, setFilteredFaqs] = useState<Array<Resource>>([])
  const [filteredThermostatReleases, setFilteredThermostatReleases] = useState<Array<Resource>>([])
  const [searchValue, setSearchValue] = useState('')
  const [showSpinner, setShowSpinner] = useState(false)
  const title = useRef(null)
  useAutoFocus({ element: title })

  const onSearchClicked = () => {
    if (searchValue.length > 0) {
      setShowSpinner(true)
      const videoResources: Array<Resource> = []
      const articleResources: Array<Resource> = []
      const faqsResources: Array<Resource> = []
      const thermostatReleaseResources: Array<Resource> = []
      videos.forEach((video) => {
        if (video.name.toLowerCase().includes(searchValue.toLowerCase())) {
          videoResources.push(video)
        }
      })
      articles.forEach((resource) => {
        if (resource.name.toLowerCase().includes(searchValue.toLowerCase())) {
          articleResources.push(resource)
        }
      })
      faqs.forEach((faq) => {
        if (faq.name.toLowerCase().includes(searchValue.toLowerCase())) {
          faqsResources.push(faq)
        }
      })

      thermostatReleases.forEach((release) => {
        if (release.version && (release.version.toLowerCase().includes(searchValue.toLowerCase()) || release.description.toLowerCase().includes(searchValue.toLowerCase()))) {
          thermostatReleaseResources.push(release)
        }
      })

      setFilteredVideos(videoResources)
      setFilteredArticles(articleResources)
      setFilteredFaqs(faqsResources)
      setFilteredThermostatReleases(thermostatReleaseResources)
      setDisplayFiltered(true)
      setShowSpinner(false)
    }
  }

  const onClickClearIconClicked = () => {
    setSearchValue('')
    setFilteredVideos([])
    setFilteredArticles([])
    setFilteredFaqs([])
    setFilteredThermostatReleases([])
    setDisplayFiltered(false)
    setShowSpinner(false)
  }

  const tabItems = [
    {
      key: '0',
      header: 'Videos',
      badge: displayFiltered ? filteredVideos.length : -1,
      details: <ResourceTable type="video" searchText={searchValue} resources={displayFiltered ? filteredVideos : videos} isLoaded={areResourcesLoaded} />
    },
    {
      key: '1',
      header: 'Articles',
      badge: displayFiltered ? filteredArticles.length : -1,
      details: <ResourceTable type="article" searchText={searchValue} resources={displayFiltered ? filteredArticles : articles} isLoaded={areResourcesLoaded} />
    },
    {
      key: '2',
      header: 'FAQs',
      badge: displayFiltered ? filteredFaqs.length : -1,
      details: <ResourceTable type="faq" searchText={searchValue} resources={displayFiltered ? filteredFaqs : faqs || []} isLoaded={areResourcesLoaded} />
    },
    {
      key: '3',
      header: <S.Header displayfiltered={displayFiltered.toString()}>Thermostat Releases</S.Header>,
      badge: displayFiltered ? filteredThermostatReleases.length : -1,
      details: <ResourceTable type="thermostat" searchText={searchValue} resources={displayFiltered ? filteredThermostatReleases : thermostatReleases || []} isLoaded={areResourcesLoaded} />
    }
  ]

  const filteredTabs = (tab) => {
    if (tab.key === '0' && videos.length > 0) {
      return tab
    } else if (tab.key === '1' && articles.length > 0) {
      return tab
    } else if (tab.key === '2' && faqs.length > 0) {
      return tab
    } else if (tab.key === '3' && thermostatReleases.length > 0) {
      return tab
    } else {
      return true
    }
  }

  const tabsWithResources = tabItems.filter((tab) => filteredTabs(tab))

  return (
    <S.Container>
      <S.PageHeader>
        <PageTitle ref={title}>Resources</PageTitle>
      </S.PageHeader>

      <S.Search
        value={searchValue}
        placeholder="Search"
        ariaLabel="resources search results"
        onChange={setSearchValue}
        onSearch={onSearchClicked}
        onClickClearIcon={onClickClearIconClicked}
        showSpinner={showSpinner}
      />

      <S.Body>
        <Tabs
          activeTabIndex={activeTabIndex}
          onSelectTabIndexCallback={(index) => setActiveTabIndex(index)}
          tabItems={tabsWithResources}
        />
      </S.Body>
    </S.Container>
  )
}

export default Resources

const S = {
  Search: styled(Search)`
    width: 395px;
    margin: 15px 0;
    padding-left: 8px;
    @media ${screenSize.small} {
      display: block;
      width: 100%;
    }
  `,
  Container: styled.div`
    width: 100%;
  `,
  Body: styled.div`
    margin: 10px;
    padding: 10px;
    background-color: ${color('paneBackground')};
  `,
  PageHeader: styled(PageHeader)`
    @media ${screenSize.small} {
      display: none;
    }
  `,
  Header: styled.div<{ displayfiltered?: string }>`
    padding: ${({ displayfiltered }) => displayfiltered === 'true' ? '0px' : '0px 15px'};}
  `
}
